// How two data points go together across places: the finding in words (src/relate.ts does the statistics), the
// numbers behind it, and a scatter plot. Opened from Ask ("do pricier ZIPs have more flood risk?") or by link:
// #/relate?level=zip&metric=<x>&y=<y>&within=<state|metro|county>
import { api, type Level, type Metric, type Relation } from "./api.ts";
import { fmt, LEVEL_LABEL, metricPeriod } from "../shared/format.ts";
import { el, nodes } from "./dom.ts";
import { metricSelect } from "./widgets.ts";

export interface RelateDeps {
  metrics: Map<string, Metric>;
  get(): { level: Level; x: string; y: string | null; within: string | null };
  set(p: Partial<{ level: Level; x: string; y: string; within: string | null }>): void;
  onOpenArea(geoId: string): void;
  onClose(): void;
}

const SVG = "http://www.w3.org/2000/svg";
const svg = <K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, ...kids: Array<SVGElement | string>) => {
  const e = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  for (const c of kids) e.append(c);
  return e;
};

/** The 1st and 99th percentiles: a few extreme places would otherwise squash everyone else into a corner. */
export function clipRange(v: number[]): [number, number] {
  const s = [...v].sort((a, b) => a - b);
  const at = (p: number) => s[Math.min(s.length - 1, Math.max(0, Math.round(p * (s.length - 1))))]!;
  const lo = at(0.01), hi = at(0.99);
  return lo === hi ? [lo - 1, hi + 1] : [lo, hi];
}

export class RelateView {
  private seq = 0;
  constructor(private readonly root: HTMLElement, private readonly d: RelateDeps) {}

  get open() { return !this.root.hidden; }
  show() { this.root.hidden = false; void this.render(); }
  hide() { this.root.hidden = true; }
  refresh() { if (this.open) void this.render(); }

  private select(value: string, onchange: (id: string) => void, label: string) {
    return metricSelect({ metrics: this.d.metrics.values(), value, onChange: onchange, label, include: (m) => !m.personal });
  }

  private async render() {
    const seq = ++this.seq;
    const p = this.d.get();
    const mx = this.d.metrics.get(p.x), my = p.y ? this.d.metrics.get(p.y) : undefined;
    const head = el("header", { class: "table-head" },
      el("div", {},
        el("h2", {}, "How two data points relate"),
        el("div", { class: "muted small" }, "Across places, by rank; also among neighbors in the same county or state. Association, not cause.")),
      el("div", { class: "panel-actions" }, el("button", { class: "btn icon", "aria-label": "Close", onclick: () => this.d.onClose() }, "✕")));
    const levels = (["zip", "city", "county", "metro", "state"] as Level[]).filter((l) => mx?.levels.includes(l) && (!my || my.levels.includes(l)));
    const controls = el("div", { class: "relate-controls" },
      this.select(p.x, (id) => this.d.set({ x: id }), "First data point"),
      el("span", { class: "muted" }, "vs."),
      this.select(p.y ?? "", (id) => this.d.set({ y: id }), "Second data point"),
      el("div", { class: "level-switch" }, ...levels.map((l) => el("button", { class: `chip${l === p.level ? " active" : ""}`, onclick: () => this.d.set({ level: l }) }, LEVEL_LABEL[l]!))),
      p.within ? el("button", { class: "chip", title: "Show all places", onclick: () => this.d.set({ within: null }) }, `in ${p.within} ✕`) : null);
    const body = el("div", { class: "compare-body" }, controls, el("p", { class: "muted small" }, "Computing…"));
    this.root.replaceChildren(head, body);
    if (!mx || !my) {
      body.replaceChildren(controls, el("p", { class: "muted" }, "Pick two data points."));
      return;
    }
    let r: Relation;
    try {
      r = await api.relate(p.level, mx.id, my.id, p.within);
    } catch (e) {
      if (seq === this.seq) body.replaceChildren(controls, el("p", { class: "panel-error small" }, (e as Error).message));
      return;
    }
    // the area's name is one more await: fetch it before the seq check so a newer view can't be overwritten after it
    const areaName = r.area && p.within ? (await api.geo(r.area).catch(() => null))?.name ?? r.area : null;
    if (seq !== this.seq) return;
    if (areaName) controls.lastElementChild!.textContent = `in ${areaName} ✕`;
    const f2 = (v: number) => `${v >= 0 ? "+" : "−"}${Math.abs(v).toFixed(2)}`;
    const stats = r.across ? el("div", { class: "relate-stats small muted" },
      `Rank correlation across places ${f2(r.across.value)} (95% range ${f2(r.across.lo)} to ${f2(r.across.hi)})`,
      r.within ? ` · within the same ${r.group} ${f2(r.within.value)} (${f2(r.within.lo)} to ${f2(r.within.hi)}, ${r.within.groups} ${r.group === "county" ? "counties" : "states"})` : "",
      ` · ${r.coverage.places.toLocaleString()} of ${r.coverage.of.toLocaleString()} ${LEVEL_LABEL[r.level]} areas have both values`,
      r.periods.x || r.periods.y ? ` · latest ${metricPeriod(mx, r.periods.x)} / ${metricPeriod(my, r.periods.y)}` : "",
      r.clusters != null ? ` · ranges from resampling whole ${r.group === "county" ? "counties" : "states"}` : "") : null;
    body.replaceChildren(...nodes(controls, el("p", { class: "relate-text" }, r.text), stats, r.points.length >= 2 ? this.scatter(r, mx, my) : null));
  }

  private scatter(r: Relation, mx: Metric, my: Metric) {
    const W = 720, H = 420, L = 70, B = 44, T = 12, R = 16;
    const [x0, x1] = clipRange(r.points.map((p) => p.x)), [y0, y1] = clipRange(r.points.map((p) => p.y));
    const sx = (v: number) => L + ((Math.min(Math.max(v, x0), x1) - x0) / (x1 - x0)) * (W - L - R);
    const sy = (v: number) => H - B - ((Math.min(Math.max(v, y0), y1) - y0) / (y1 - y0)) * (H - B - T);
    const ticks = (a: number, b: number) => [0, 0.25, 0.5, 0.75, 1].map((t) => a + t * (b - a));
    const plot = svg("svg", { viewBox: `0 0 ${W} ${H}`, class: "relate-plot", role: "img", "aria-label": `${mx.label} vs. ${my.label}` },
      ...ticks(x0, x1).map((v) => svg("g", {}, svg("line", { x1: sx(v), x2: sx(v), y1: T, y2: H - B, class: "grid" }), svg("text", { x: sx(v), y: H - B + 16, "text-anchor": "middle", class: "tick" }, fmt(mx, v, true)))),
      ...ticks(y0, y1).map((v) => svg("g", {}, svg("line", { x1: L, x2: W - R, y1: sy(v), y2: sy(v), class: "grid" }), svg("text", { x: L - 6, y: sy(v) + 4, "text-anchor": "end", class: "tick" }, fmt(my, v, true)))),
      svg("text", { x: (L + W - R) / 2, y: H - 6, "text-anchor": "middle", class: "axis" }, mx.label),
      svg("text", { x: 14, y: (T + H - B) / 2, "text-anchor": "middle", class: "axis", transform: `rotate(-90 14 ${(T + H - B) / 2})` }, my.label),
      ...r.points.map((p) => {
        const c = svg("circle", { cx: sx(p.x), cy: sy(p.y), r: 3, class: "pt" }, svg("title", {}, `${p.name}: ${fmt(mx, p.x)}, ${fmt(my, p.y)}`));
        c.addEventListener("click", () => this.d.onOpenArea(p.geo_id));
        return c;
      }));
    const thinned = r.points.length < r.coverage.places ? ` Every ${Math.round(r.coverage.places / r.points.length)}th place is drawn.` : "";
    return el("section", { class: "card compare-card" }, plot,
      el("div", { class: "muted small" }, `Axes run from the 1st to the 99th percentile; places beyond sit on the edge.${thinned} Click a dot to open the place.`));
  }
}
